import FormFooter from "@/components/form-footer";
import cn from "@/utils/class-names";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MediasPart from "./medias-part";
import AdditionalContentPart from "./additional-content-part";
import { routes } from "@/config/routes";
import PageHeader from "@/app/shared/page-header";
import InformationProjectPart from "./information-project";
import { Tab } from "rizzui";
import useApi from "@/hooks/useApi";
import {
  CreateProjectInput,
  createProjectSchema,
} from "@/utilities/validators/projects";
import { zodResolver } from "@hookform/resolvers/zod";
import ProjectRepository from "@/utilities/repositories/Project";
import { filterChangedFormFields } from "@/utils/form";
import toast from "react-hot-toast";
import { Roles } from "@/utilities/types/Users.ts";
import useProfile from "@/hooks/use-profile.ts";
import { useState } from "react";
import { isGoogleMapsIframe, parseSrcFromIframe } from "@/utils/string";
import { backPrev } from "@/utilities/functions";
import _ from "lodash";
import { EProjectWhatIsNextStatus } from "@/utilities/types/Project";

export interface CreateEditProjectProps {
  className?: string;
}

const UNSAVED_CHANGES_MESSAGE =
  "You have unsaved changes. Are you sure you want to leave this page?";
const UNSAVED_CHANGES_TOAST_ID = "project-unsaved-changes";

export default function CreateEditProject({
  className,
}: CreateEditProjectProps) {
  const methods = useForm<CreateProjectInput>({
    resolver: zodResolver(createProjectSchema),
    reValidateMode: "onChange",
    mode: "all",
    defaultValues: {
      whatIsNextStatus: EProjectWhatIsNextStatus.PreLaunch,
    },
  });
  const [slug, setSlug] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const hasUnsavedHistoryTrapRef = React.useRef(false);
  const isLeavingPageRef = React.useRef(false);
  const skipNextPopConfirmRef = React.useRef(false);

  const { request: onCreateProject, loading: loadingCreate } = useApi({
    enableToast: true,
    request: ProjectRepository.createProject,
  });

  const { request: getDetailProjectById } = useApi({
    request: ProjectRepository.getProjectById,
  });

  const { request: onEditProject, loading: loadingEdit } = useApi({
    request: ProjectRepository.updateProject,
    enableToast: true,
  });

  const handleCreateProject = (data: CreateProjectInput) => {
    const {
      pictures,
      targetPreviewLaunchDateMonth,
      targetPreviewLaunchDateYear,
      heroImage,
      locationImage,
      educationImage,
      transportImage,
      amenityImage,
      ...restData
    } = data;
    const mediaIds = data.medias?.map((media) => media.id);
    const sitePlanImagesId = data?.siteplanImages?.map((media) => media?.id);
    const convertData = {
      ...restData,
      previewDate:
        targetPreviewLaunchDateMonth || targetPreviewLaunchDateYear
          ? JSON.stringify({
              month: targetPreviewLaunchDateMonth || "",
              year: targetPreviewLaunchDateYear || "",
            })
          : undefined,
      desktopLogo: undefined,
      desktopLogoId: data?.desktopLogo?.[0]?.id,
      brochureAsset: undefined,
      brochureAssetId: data?.brochureAsset?.[0]?.id,
      siteplanImages: undefined,
      siteplanImageIds: sitePlanImagesId,
      pictures: undefined,
      photoId: pictures?.[0]?.id || null,
      medias: mediaIds,
      slug: restData?.slug ? restData?.slug : undefined,
      isShowflatLocation: data?.isShowflatLocation || false,
      heroImageId: Array.isArray(heroImage)
        ? heroImage?.[0]?.id || null
        : undefined,
      locationImageId: Array.isArray(locationImage)
        ? locationImage?.[0]?.id || null
        : undefined,
      educationImageId: Array.isArray(educationImage)
        ? educationImage?.[0]?.id || null
        : undefined,
      transportImageId: Array.isArray(transportImage)
        ? transportImage?.[0]?.id || null
        : undefined,
      amenityImageId: Array.isArray(amenityImage)
        ? amenityImage?.[0]?.id || null
        : undefined,
    };
    onCreateProject(convertData).then(() => {
      handleBack(true);
    });
  };

  const handleUpdateProject = (data: CreateProjectInput) => {
    const dirtyUpdateFields: Partial<CreateProjectInput> =
      filterChangedFormFields(data, methods?.formState.dirtyFields);
    const {
      pictures,
      desktopLogo,
      siteplanImages,
      brochureAsset,
      heroImage,
      locationImage,
      educationImage,
      transportImage,
      amenityImage,
      targetPreviewLaunchDateMonth,
      targetPreviewLaunchDateYear,
      ...restData
    } = dirtyUpdateFields;
    const isPreviewDateDirty =
      targetPreviewLaunchDateMonth !== undefined ||
      targetPreviewLaunchDateYear !== undefined;

    let mediaIds = _.unionBy(data?.medias?.map((media) => media?.id));
    const sitePlanImagesId = siteplanImages?.map((media) => media?.id);
    const isDirtyPicture = methods?.formState.dirtyFields?.pictures;
    const photoId = isDirtyPicture ? pictures?.[0]?.id || null : undefined;

    if (photoId && mediaIds.includes(photoId)) {
      mediaIds = mediaIds.filter((id) => id !== photoId);
    }

    const convertData = {
      ...restData,
      ...(isPreviewDateDirty && {
        previewDate:
          data.targetPreviewLaunchDateMonth || data.targetPreviewLaunchDateYear
            ? JSON.stringify({
                month: data.targetPreviewLaunchDateMonth || "",
                year: data.targetPreviewLaunchDateYear || "",
              })
            : "",
      }),
      manualAvailableUnitsCount: data.manualAvailableUnitsCount,
      photoId: photoId,
      medias: mediaIds,
      desktopLogoId: Array.isArray(desktopLogo)
        ? desktopLogo?.[0]?.id || null
        : undefined,
      siteplanImageIds: sitePlanImagesId,
      brochureAssetId: Array.isArray(brochureAsset)
        ? brochureAsset?.[0]?.id || null
        : undefined,
      heroImageId: Array.isArray(heroImage)
        ? heroImage?.[0]?.id || null
        : undefined,
      locationImageId: Array.isArray(locationImage)
        ? locationImage?.[0]?.id || null
        : undefined,
      educationImageId: Array.isArray(educationImage)
        ? educationImage?.[0]?.id || null
        : undefined,
      transportImageId: Array.isArray(transportImage)
        ? transportImage?.[0]?.id || null
        : undefined,
      amenityImageId: Array.isArray(amenityImage)
        ? amenityImage?.[0]?.id || null
        : undefined,
      googleMapUrl: isGoogleMapsIframe(data?.googleMapUrl || "")
        ? parseSrcFromIframe(data?.googleMapUrl || "") || ""
        : data?.googleMapUrl || "",
      slug: restData?.slug ? restData?.slug : undefined,
    };

    onEditProject(id, convertData).then(() => {
      handleBack(true);
    });
  };
  let RedirectProject: string = "";
  const onSubmit = (data: CreateProjectInput) => {
    if (isUpdate) {
      handleUpdateProject(data);
    } else {
      handleCreateProject(data);
    }
  };
  const isLoading = loadingCreate || loadingEdit;
  const shouldWarnBeforeLeaving = methods.formState.isDirty && !isLoading;

  const showUnsavedChangesToast = React.useCallback(() => {
    return new Promise<boolean>((resolve) => {
      toast.custom(
        (t) => (
          <div
            className={cn(
              "w-[calc(100vw-32px)] max-w-md rounded border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-200 dark:bg-gray-50",
              t.visible
                ? "animate-in fade-in-0 zoom-in-95"
                : "animate-out fade-out-0 zoom-out-95",
            )}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="unsaved-project-title"
            aria-describedby="unsaved-project-description"
          >
            <h3
              id="unsaved-project-title"
              className="text-base font-semibold text-gray-900"
            >
              Unsaved changes
            </h3>
            <p
              id="unsaved-project-description"
              className="mt-1 text-sm text-gray-600"
            >
              {UNSAVED_CHANGES_MESSAGE}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                onClick={() => {
                  toast.remove(t.id);
                  resolve(false);
                }}
              >
                Stay
              </button>
              <button
                type="button"
                className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                onClick={() => {
                  toast.remove(t.id);
                  resolve(true);
                }}
              >
                Leave
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          id: UNSAVED_CHANGES_TOAST_ID,
          position: "top-center",
        },
      );
    });
  }, []);

  const handleBack = async (skipUnsavedCheck = false) => {
    if (
      !skipUnsavedCheck &&
      shouldWarnBeforeLeaving &&
      !(await showUnsavedChangesToast())
    ) {
      return;
    }

    methods.reset();

    navigate(backPrev(routes.projectListing));
  };

  const { id } = useParams();

  const isUpdate = !!id;
  const { profile } = useProfile();
  const title =
    profile?.role === Roles.AGENCY
      ? "Project Information"
      : isUpdate
        ? "Edit Project"
        : "Create Project";
  if (profile?.domains && profile?.domains.length > 0) {
    RedirectProject = profile?.domains[0].name;
  }
  const pageHeader = {
    title,
    breadcrumb: [
      {
        href: routes.project.listing,
        name: "Projects",
      },
      {
        name: title,
      },
    ],
  };

  React.useEffect(() => {
    if (!shouldWarnBeforeLeaving) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isLeavingPageRef.current) return;

      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldWarnBeforeLeaving]);

  React.useEffect(() => {
    if (!shouldWarnBeforeLeaving) return;

    const currentPath = `${location.pathname}${location.search}${location.hash}`;

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      const anchor = (event.target as Element | null)?.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      const nextUrl = new URL(anchor.href);
      if (nextUrl.origin !== window.location.origin) return;

      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      event.stopPropagation();

      showUnsavedChangesToast().then((canLeave) => {
        if (canLeave) {
          isLeavingPageRef.current = true;
          methods.reset();
          navigate(nextPath);
        }
      });
    };

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [
    location.hash,
    location.pathname,
    location.search,
    methods,
    navigate,
    showUnsavedChangesToast,
    shouldWarnBeforeLeaving,
  ]);

  React.useEffect(() => {
    if (shouldWarnBeforeLeaving && !hasUnsavedHistoryTrapRef.current) {
      window.history.pushState(
        {
          ...window.history.state,
          projectUnsavedChangesTrap: true,
        },
        "",
        window.location.href,
      );
      hasUnsavedHistoryTrapRef.current = true;
    }

    if (!shouldWarnBeforeLeaving) {
      hasUnsavedHistoryTrapRef.current = false;
    }
  }, [shouldWarnBeforeLeaving]);

  React.useEffect(() => {
    if (!shouldWarnBeforeLeaving) return;

    const handlePopState = () => {
      if (skipNextPopConfirmRef.current) {
        skipNextPopConfirmRef.current = false;
        return;
      }

      window.history.pushState(
        {
          ...window.history.state,
          projectUnsavedChangesTrap: true,
        },
        "",
        window.location.href,
      );
      hasUnsavedHistoryTrapRef.current = true;

      showUnsavedChangesToast().then((canLeave) => {
        if (canLeave) {
          isLeavingPageRef.current = true;
          methods.reset();
          hasUnsavedHistoryTrapRef.current = false;
          skipNextPopConfirmRef.current = true;
          window.history.go(-2);
        }
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [methods, shouldWarnBeforeLeaving, showUnsavedChangesToast]);

  React.useEffect(() => {
    if (!isUpdate) return;
    getDetailProjectById(id)
      .then((response) => {
        if (!response) return;
        const { data } = response;
        if (!data) return;
        setSlug(data.slug);

        const photos = data?.photo
          ? {
              id: data?.photo?.id,
              url: data?.photo?.urls?.[0]?.url,
              type: data?.photo?.type,
              name: data?.photo?.id,
              disabled: false,
            }
          : undefined;

        const desktopLogo = data?.desktopLogo
          ? {
              id: data?.desktopLogo?.id,
              url: data?.desktopLogo?.urls?.[0]?.url,
              type: data?.desktopLogo?.type,
              name: data?.desktopLogo?.id,
              disabled: false,
            }
          : undefined;

        const sitePlanImages = data?.siteplanImages
          ?.filter((media) => media.id !== data?.photo?.id)
          ?.map((media) => ({
            id: media?.id,
            url: (media?.type === "video"
              ? media?.urls?.[0]
              : media?.urls?.[0]?.url) as string,
            type: media?.type,
            name: media?.id,
          }));

        const sitePlanImagesId = data?.siteplanImages?.map((media) => media.id);

        const medias = data?.medias
          ?.filter(
            (media) =>
              media.id !== data?.photo?.id &&
              !sitePlanImagesId?.includes(media.id) &&
              media.type !== "video",
          )
          ?.map((media) => ({
            // medias = photo + medias uploaded => need filter the photo
            // filter video type so only images show in gallery
            id: media?.id,
            url: media?.urls?.[0]?.url as string,
            type: media?.type,
            name: media?.id,
            disabled: false,
          }));

        // if (data?.photo) {
        //   // Push thumbnail to the first of medias
        //   medias.unshift({
        //     id: data?.photo?.id,
        //     url: data?.photo?.urls?.[0]?.url,
        //     type: EUploadType.IMAGE,
        //     name: data?.photo?.id,
        //     disabled: true,
        //   });
        // }

        const brochureAsset = data?.brochureAsset
          ? {
              id: data?.brochureAsset?.id,
              url: data?.brochureAsset?.urls?.[0]?.url,
              type: data?.brochureAsset?.type,
              name: data?.brochureAsset?.id,
              disabled: false,
            }
          : undefined;

        const heroImage = data?.heroImage
          ? {
              id: data?.heroImage?.id,
              url: data?.heroImage?.urls?.[0]?.url,
              type: data?.heroImage?.type,
              name: data?.heroImage?.id,
              disabled: false,
            }
          : undefined;

        const locationImage = data?.locationImage
          ? {
              id: data?.locationImage?.id,
              url: data?.locationImage?.urls?.[0]?.url,
              type: data?.locationImage?.type,
              name: data?.locationImage?.id,
              disabled: false,
            }
          : undefined;

        const educationImage = data?.educationImage
          ? {
              id: data?.educationImage?.id,
              url: data?.educationImage?.urls?.[0]?.url,
              type: data?.educationImage?.type,
              name: data?.educationImage?.id,
              disabled: false,
            }
          : undefined;

        const transportImage = data?.transportImage
          ? {
              id: data?.transportImage?.id,
              url: data?.transportImage?.urls?.[0]?.url,
              type: data?.transportImage?.type,
              name: data?.transportImage?.id,
              disabled: false,
            }
          : undefined;

        const amenityImage = data?.amenityImage
          ? {
              id: data?.amenityImage?.id,
              url: data?.amenityImage?.urls?.[0]?.url,
              type: data?.amenityImage?.type,
              name: data?.amenityImage?.id,
              disabled: false,
            }
          : undefined;

        let monthPart = "";
        let yearPart: string | undefined = undefined;

        if (data?.previewDate) {
          try {
            const parsedPreviewDate = JSON.parse(data.previewDate);
            monthPart = parsedPreviewDate?.month || "";
            yearPart = parsedPreviewDate?.year || undefined;
          } catch (e) {
            // Fallback for old string format (e.g., "Mar/April 2026")
            const previewDateParts = data.previewDate.trim().split(" ");
            if (previewDateParts.length > 1) {
              yearPart = previewDateParts.pop();
              monthPart = previewDateParts.join(" ");
            } else {
              monthPart = data.previewDate;
            }
          }
        }

        const defaultData: Partial<CreateProjectInput> = {
          area: data?.area,
          categoryId: data?.category?.id,
          description: data?.description || "",
          locationId: data?.location?.id,
          developerId: data?.developer?.id,
          marketSegment: data?.marketSegment || "",
          name: typeof data?.name === "object" ? data?.name?.en : data?.name,
          expectedTop: data?.expectedTop
            ? new Date(data?.expectedTop)
            : undefined,
          targetPreviewLaunchDateMonth: monthPart || undefined,
          targetPreviewLaunchDateYear: yearPart || undefined,
          tenure: data?.tenure || "",
          tenureEffectFrom: data?.tenureEffectFrom
            ? new Date(data?.tenureEffectFrom)
            : null,
          pictures: photos && [photos],
          medias,
          amenityHtml: data?.amenityHtml || "",
          facilityHtml: data?.facilityHtml || "",
          directCommission: data?.directCommission,
          googleMapUrl: data?.googleMapUrl || "",
          address: data?.address || "",
          totalUnitsCount: data?.totalUnitsCount ?? "",
          availableUnitsCount: data?.availableUnitsCount ?? "",
          manualTotalUnitsCount: data?.manualTotalUnitsCount ?? "",
          manualAvailableUnitsCount: data?.manualAvailableUnitsCount ?? "",
          desktopLogo: desktopLogo && [desktopLogo],
          siteplanImages: sitePlanImages,
          whatIsNextStatus:
            data?.whatIsNextStatus || EProjectWhatIsNextStatus.PreLaunch,
          brochureAsset: brochureAsset && [brochureAsset],
          heroImage: heroImage && [heroImage],
          locationImage: locationImage && [locationImage],
          educationImage: educationImage && [educationImage],
          transportImage: transportImage && [transportImage],
          amenityImage: amenityImage && [amenityImage],
          slug: data?.slug,
          showflatLocation: data?.showflatLocation || "",
          isShowflatLocation: data?.isShowflatLocation || false,
          // Hero Content
          catchphrase: data?.catchphrase || "",
          headline: data?.headline || "",
          shortDescription: data?.shortDescription || "",
          // Site Plan
          sitePlanTitle: data?.sitePlanTitle || "",
          sitePlanDescription: data?.sitePlanDescription || "",
          // Location
          locationTitle: data?.locationTitle || "",
          // Education
          educationTitle: data?.educationTitle || "",
          educationDescription: data?.educationDescription || "",
          // Transport
          transportTitle: data?.transportTitle || "",
          transportDescription: data?.transportDescription || "",
          // Amenity
          amenityTitle: data?.amenityTitle || "",
          amenityDescription: data?.amenityDescription || "",
          // SEO
          seoText: data?.seoText || "",
        };

        methods.reset(defaultData);
      })
      .catch(() => {
        toast.error("Project not found!");
        handleBack();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const picturesWatch = methods.watch("pictures");
  React.useEffect(() => {
    // 3 cases:
    // 1. Delete photo => remove thumbnail from medias
    // 2. Add new photo => add thumbnail to the first of medias
    // 3. Update photo => update thumbnail of medias

    const mediasUpdate = [...(methods.getValues("medias") || [])].filter(
      (item) => !item.disabled,
    ); // always remove thumbnail if existing

    // if (picturesWatch?.length && picturesWatch.length > 0) {
    //   // add thumbnail to the first of medias in add or update case
    //   mediasUpdate.unshift({
    //     id: picturesWatch?.[0]?.id,
    //     url: picturesWatch?.[0]?.url,
    //     type: picturesWatch?.[0]?.type,
    //     name: picturesWatch?.[0]?.name,
    //     disabled: true, // is
    //   });
    // }

    methods.setValue(
      "medias",
      _.uniqBy(mediasUpdate, (item) => item.id),
    );
  }, [picturesWatch, methods]);
  return (
    <div className="@container h-full mt-4">
      <PageHeader
        title={pageHeader.title}
        breadcrumb={pageHeader.breadcrumb}
      ></PageHeader>
      <FormProvider {...methods}>
        <form
          className={cn("md:mt-6 mt-0", className)}
          noValidate
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <Tab>
            <Tab.List>
              <Tab.ListItem>Basic Content</Tab.ListItem>
              <Tab.ListItem>Additional Content</Tab.ListItem>
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <div className="pt-0 md:pt-7 @2xl:pt-9 @3xl:pt-11">
                  <InformationProjectPart />
                </div>
                <div
                  className={`pt-0 md:pt-7 @2xl:pt-9 @3xl:pt-11 ${profile?.role === Roles.AGENCY ? "[&_.upload-zone]:opacity-80 [&_.upload-zone]:pointer-events-none " : ""}`}
                >
                  <MediasPart />
                </div>
              </Tab.Panel>
              <Tab.Panel>
                <div
                  className={`pt-0 md:pt-7 @2xl:pt-9 @3xl:pt-11 ${profile?.role === Roles.AGENCY ? "[&_.upload-zone]:opacity-80 [&_.upload-zone]:pointer-events-none " : ""}`}
                >
                  <AdditionalContentPart />
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab>
          <FormFooter
            isLoading={isLoading}
            altBtnText="Back"
            submitBtnText={isUpdate ? "Save" : "Add"}
            handleAltBtn={() => handleBack()}
            submitButtonProps={{
              disabled: isLoading || !methods.formState.isDirty,
            }}
            redirectPage={`https://${RedirectProject}/projects/${slug}`}
            canSubmit={profile?.role === Roles.ADMIN}
          />
        </form>
      </FormProvider>
    </div>
  );
}
